import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  guestsService,
  type GuestInvite,
  type GuestPermission,
  type GuestScope,
} from '../services/guests'

interface GuestInviteModalProps {
  open: boolean
  onClose: () => void
  scope: GuestScope
  scopeId: number
}

export function GuestInviteModal({ open, onClose, scope, scopeId }: GuestInviteModalProps) {
  const [invites, setInvites] = useState<GuestInvite[]>([])
  const [email, setEmail] = useState('')
  const [permission, setPermission] = useState<GuestPermission>('read')
  const [days, setDays] = useState(14)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    guestsService.list(scope, scopeId).then(setInvites).catch(() => setInvites([]))
  }, [open, scope, scopeId])

  if (!open) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setBusy(true)
    try {
      const inv = await guestsService.create({
        email: email.trim(),
        scope_entity: scope,
        scope_id: scopeId,
        permission,
        expires_in_days: days,
      })
      setInvites(prev => [inv, ...prev])
      setEmail('')
      toast.success('Invite sent')
    } catch {
      toast.error('Failed to send invite')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (id: number) => {
    if (!confirm('Revoke this invite?')) return
    try {
      await guestsService.revoke(id)
      setInvites(prev => prev.map(i => (i.id === id ? { ...i, revoked: true } : i)))
    } catch {
      toast.error('Failed to revoke')
    }
  }

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied')
    } catch {
      toast.error('Failed to copy')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-lg bg-[var(--color-surface)] p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
            Invite guest collaborator
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] text-[var(--color-text-secondary)]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-text-secondary)]">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="client@example.com"
              className="min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-text-secondary)]">Permission</span>
              <select
                value={permission}
                onChange={e => setPermission(e.target.value as GuestPermission)}
                className="min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
              >
                <option value="read">Read only</option>
                <option value="comment">Can comment</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-text-secondary)]">Expires in (days)</span>
              <input
                type="number"
                min={1}
                max={180}
                value={days}
                onChange={e => setDays(Number(e.target.value))}
                className="min-h-[44px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="min-h-[44px] rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>

        {invites.length > 0 && (
          <div className="mt-5 border-t border-[var(--color-border)] pt-4">
            <h3 className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">
              Existing invites
            </h3>
            <ul className="flex flex-col gap-2">
              {invites.map(inv => (
                <li
                  key={inv.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-[var(--color-text-primary)]">
                      {inv.email}
                    </p>
                    <p className="text-xs text-[var(--color-text-secondary)]">
                      {inv.permission} ·{' '}
                      {inv.revoked
                        ? 'revoked'
                        : `expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  {!inv.revoked && (
                    <>
                      <button
                        type="button"
                        onClick={() => copyLink(inv.invite_url)}
                        className="min-h-[36px] rounded-md border border-[var(--color-border)] px-2 py-1 text-xs"
                      >
                        Copy link
                      </button>
                      <button
                        type="button"
                        onClick={() => revoke(inv.id)}
                        className="min-h-[36px] rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-danger)]"
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
